import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Admin-only: correct a resident's flat number(s) and joint-holder order from the Directory.
export const EditResidentFlat = ({ resident, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [flats, setFlats] = useState('');
  const [position, setPosition] = useState('none');
  const [busy, setBusy] = useState(false);

  const start = () => {
    setFlats((resident.units || []).join(', '));
    setPosition(resident.holder_position ? String(resident.holder_position) : 'none');
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/api/memberships/admin/${resident.membership_id}/flat`, {
        units: flats.split(',').map((s) => s.trim()).filter(Boolean),
        holder_position: position === 'none' ? null : Number(position),
      });
      toast.success(`Updated ${resident.name}`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={start} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground" title="Edit flat & holder order"
        data-testid={`edit-resident-${resident.id}`}>
        <Pencil className="h-3.5 w-3.5" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="edit-resident-dialog">
          <DialogHeader>
            <DialogTitle>Edit {resident.name}</DialogTitle>
            <DialogDescription>Use the format W9-202 (wing, then flat). Holder order follows the agreement — 1st = first-named owner.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-flats">Flat number(s), comma-separated</Label>
              <Input id="edit-flats" value={flats} onChange={(e) => setFlats(e.target.value)} placeholder="W9-202" data-testid="edit-resident-flats" />
            </div>
            <div className="space-y-1.5">
              <Label>Holder order on agreement</Label>
              <Select value={position} onValueChange={setPosition}>
                <SelectTrigger data-testid="edit-resident-position"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  <SelectItem value="1">1st holder (first-named)</SelectItem>
                  <SelectItem value="2">2nd holder</SelectItem>
                  <SelectItem value="3">3rd holder</SelectItem>
                  <SelectItem value="4">4th holder</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy || !flats.trim()} data-testid="edit-resident-save">
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
